import { useState, useEffect } from 'react';
import { MapPin, Home, Briefcase, Edit2, Trash2, Plus, Star } from 'lucide-react';
import { savedAddressService, SavedAddress } from '@/services/hereLocationService';
import { LocationInput } from './LocationInput';

export function SavedAddressManager() {
  const [addresses, setAddresses] = useState<SavedAddress[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newAddress, setNewAddress] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newCoords, setNewCoords] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = () => {
    setAddresses(savedAddressService.getSavedAddresses());
  };

  const handleAddAddress = () => {
    if (!newAddress.trim() || !newLabel.trim() || !newCoords) return;

    savedAddressService.saveAddress({
      label: newLabel.trim(),
      address: newAddress,
      coordinates: newCoords,
      isDefault: addresses.length === 0,
    });

    setNewAddress('');
    setNewLabel('');
    setNewCoords(null);
    setIsAdding(false);
    loadAddresses();
  };

  const handleDeleteAddress = (id: string) => {
    if (confirm('Are you sure you want to delete this address?')) {
      savedAddressService.deleteAddress(id);
      loadAddresses();
    }
  };

  const handleSetDefault = (id: string) => {
    savedAddressService.setDefaultAddress(id);
    loadAddresses();
  };

  const handleLocationChange = (location: string, coordinates?: { lat: number; lng: number }) => {
    setNewAddress(location);
    setNewCoords(coordinates || null);
  };

  const getIconForLabel = (label: string) => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes('home')) return <Home className="w-5 h-5 text-green-600" />;
    if (lowerLabel.includes('work') || lowerLabel.includes('office')) return <Briefcase className="w-5 h-5 text-purple-600" />;
    return <MapPin className="w-5 h-5 text-gray-600" />;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Saved Addresses</h3>
          <p className="text-sm text-gray-600">Manage your frequently used locations</p>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Address
        </button>
      </div>

      {/* Add New Address Form */}
      {isAdding && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-900 mb-4">Add New Address</h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Label
              </label>
              <input
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g., Home, Work, Mom's House"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <LocationInput
                value={newAddress}
                onChange={handleLocationChange}
                placeholder="Enter address"
                showSavedAddresses={false}
                showCurrentLocation={true}
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setIsAdding(false);
                  setNewAddress('');
                  setNewLabel('');
                  setNewCoords(null);
                }}
                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddAddress}
                disabled={!newAddress.trim() || !newLabel.trim() || !newCoords}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Save Address
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Address List */}
      <div className="space-y-3">
        {addresses.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No saved addresses yet</p>
            <p className="text-xs text-gray-400">Add your frequently used locations for quick access</p>
          </div>
        ) : (
          addresses.map((address) => (
            <div
              key={address.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center space-x-3 flex-1">
                {getIconForLabel(address.label)}
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 className="font-medium text-gray-900">{address.label}</h4>
                    {address.isDefault && (
                      <div className="flex items-center px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
                        <Star className="w-3 h-3 mr-1" />
                        Default
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{address.address}</p>
                  <p className="text-xs text-gray-400">
                    Added {new Date(address.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                {!address.isDefault && (
                  <button
                    onClick={() => handleSetDefault(address.id)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Set as default"
                  >
                    <Star className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => console.log('Edit functionality coming soon')}
                  className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  title="Edit address"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteAddress(address.id)}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete address"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {addresses.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            💡 <strong>Tip:</strong> Your saved addresses will appear as quick options when posting jobs or booking services.
          </p>
        </div>
      )}
    </div>
  );
}